<template>
  <div v-if="order">
    <b-container>
      <div class="order-summary-container p-4">
        <div class="d-flex flex-column flex-md-row justify-content-center">
          <div class="rounded-circle align-self-center px-2 py-1 border mr-2 mb-1">
            <font-awesome-icon class="text-white"  :icon="['fas', 'check']"></font-awesome-icon>
          </div>
          <h2 v-if="session.label" v-html="session.label" class="text-center"></h2>
          <h2 v-else class="text-center">Your Order has been completed!</h2>
        </div>
        <h4 class="text-center mt-3 order-no">Order # {{order.display_order_id || order.order_id}}</h4>
        <div class="text-center confirmation-email">A confirmation of your order has been sent to {{customer.email}}</div>
      </div>
    </b-container>
    <b-container class="order-summary-body px-sm-5 py-2 py-sm-4">
      <div class="order-date pl-1 d-flex">Order placed <h6 v-if="order.order_id" class="ml-1">{{getShortDate(order.created)}}</h6></div>
      <b-row class="border-bottom mt-2">
        <b-col class="mb-4">
          <div class="px-sm-4">
            <table class="border-bottom">
              <tr class="border-bottom table-header">
                <th class="w-100" >Product </th>
                <th class="text-right"> Price </th>
              </tr>
              <tr class="border-bottom" v-for="item in order.items" :key="'item-' + item.id.toString()">
                <td class="d-flex">
                  <img class="my-3 mr-2 product-img" :src="getImageUrl(item)" @error="setAltImg($event, item)" alt=""/>
                  <div class="d-flex flex-column my-3">
                    <div class="product-name">{{item.name}}</div>
                    <div class="order-summary-product-qty">Qty:{{item.quantity}}</div>
                  </div>
                </td>
                <td class="align-top">
                  <div class="my-3 product-price">{{item.price | currency(order.currency)}}</div>
                </td>
              </tr>
            </table>
            <div class="d-flex mt-2 price-detail ml-auto flex-column border-bottom">
              <div class="d-flex justify-content-between mt-2">
                <div>Sub Total</div>
                <div>{{order.subtotal | currency(order.currency)}}</div>
              </div>
              <div class="d-flex justify-content-between mt-1">
                <div>Shipping Fee (Standard)</div>
                <div>{{order.shipping_price | currency(order.currency)}}</div>
              </div>
              <div class="d-flex justify-content-between mt-1" v-if="showDiscount(order)">
                <div>Discount</div>
                <div>-{{order.discount | currency(order.currency)}}</div>
              </div>
              <div class="d-flex justify-content-between mt-1 mb-3">
                <div>Taxes</div>
                <div>{{order.tax | currency(order.currency)}}</div>
              </div>
            </div>
            <div class="d-flex price-detail ml-auto justify-content-between mt-1">
              <h6>Order Total</h6>
              <h6>{{order.total_discounted_price | currency(order.currency)}}</h6>
            </div>
            <div class="summary-btn-container price-detail" :class="!hasSupportPathUrl && 'd-block'">
              <div class="continue-btn"></div>
              <b-button @click="done()" class="continue-btn" :class="!hasSupportPathUrl && 'ml-auto w-50'" variant="primary">
                <span class="inline-button-label" v-html="input.formatted_label"></span>&nbsp;
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4" sm="6" cols="12" class="mb-sm-5 mb-2">
          <div class="d-flex flex-column mt-4 order-detail">
            <h6>Shipping Address</h6>
            <div>{{customer.ship_first_name}} {{ customer.ship_last_name}}</div>
            <div class="mt-1">
              {{customer.ship_address1}} <span v-if="customer.ship_address2">, {{customer.ship_address2}}</span>
            </div>
            <div class="mt-1">{{customer.ship_city}}, {{customer.ship_state}}, {{customer.ship_country}}, {{customer.ship_zip}}</div>
            <div class="mt-1">{{customer.email}}</div>
            <div class="mt-1">{{customer.phone}}</div>
          </div>
        </b-col>
        <b-col md="4" sm="6" class="mb-sm-5 mb-2" cols="12">
          <div class="d-flex flex-column mt-4 order-detail">
            <h6>Billing Address</h6>
            <div>{{customer.first_name}} {{ customer.last_name}}</div>
            <div class="mt-1">
              {{customer.address1}} <span v-if="customer.address2">, {{customer.address2}}</span>
            </div>
            <div class="mt-1">{{customer.city}}, {{customer.state}}, {{customer.country}}, {{customer.zip}}</div>
            <div class="mt-1">{{customer.email}}</div>
            <div class="mt-1">{{customer.phone}}</div>
          </div>
        </b-col>
        <b-col md="4" sm="6" class="mb-sm-5 mb-2" cols="12">
          <div class="d-flex flex-column mt-4 order-detail">
            <h6>Delivery Methods</h6>
            <div>Standard ({{order.shipping_price | currency(order.currency)}})</div>
          </div>
        </b-col>
        <b-col md="4" sm="6" cols="12" class="mb-sm-5 mb-2">
          <div v-if="this.paymentSource" class="d-flex flex-column mt-4">
            <h6>Payment Information</h6>
            <div v-if="paymentSource">
              <font-awesome-icon :icon="getBrandClass(this.paymentSource.type.toLowerCase())"
                                 class="footer_btn"></font-awesome-icon><span class="ml-3">**** **** **** {{this.paymentSource.last4}}</span>
            </div>
          </div>
        </b-col>
      </b-row>
      <ContactSupport v-if="showContactForm"
                      :order-summary="true"
                      :input="input"
                      :session="session"
                      :contacted="contacted"
                      @done="toggleContactSupport($event)">
      </ContactSupport>
    </b-container>
    <b-container v-if="related_products.length" class="mt-md-0 mt-3">
      <h3 class="text-center">People Also Bought</h3>
      <div class="related-products px-sm-5">
        <carousel
          ref="relatedProductsCarousel"
          class="item-status-carousel nav-enabled"
          :per-page-custom="[[0,1], [768,2], [992,3]]"
          :scroll-per-page="false"
          :centerMode="true"
          :pagination-enabled="false"
          :navigation-enabled="true"
          navigation-prev-label='<span class="carousel-control-prev-icon"></span>'
          navigation-next-label='<span class="carousel-control-next-icon"></span>'
        >
          <slide v-for="related_product in related_products" :key="related_product.id">
            <RelatedProductTile :product="related_product" :session="session"></RelatedProductTile>
          </slide>
        </carousel>
      </div>
    </b-container>
  </div>
</template>
<script>
import { Carousel, Slide } from 'vue-carousel'
import axios from 'axios'
import {getShortDate, getItemImageUrl, setFallbackImageUrl} from '../utils'
import {InputType, PaymentSource, PaymentSourceStrings} from '../constants'

const ContactSupport = () => import('./ContactSupport')
const RelatedProductTile = () => import('./RelatedProductTile')

export default {
  name: 'OrderSummary',
  components: {Carousel, Slide, RelatedProductTile, ContactSupport},
  props: {
    session: Object,
    input: Object,
    noback: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    noselect: {
      type: Boolean,
      default: function() {
        return false
      }
    }
  },
  data: function() {
    return {
      getShortDate,
      order: {},
      contacted: false,
      related_products: [],
      campaignProductIds: '',
      paymentSource: null,
      showContactForm: false
    }
  },
  computed: {
    activeItemRequired: function() {
      return this.input && this.input.requires_active_item
    },
    isCheckout: function() {
      return this.input && (this.input.type === InputType.CheckoutConfirm)
    },
    customer: function() {
      return this.session.customer
    },
    hasSupportPathUrl: function() {
      return this.session && this.session.campaign && this.session.campaign.support_url
    }
  },
  methods: {
    getBrandClass: function(brand) {
      let cardBrandToClass = {
        'jcb': ['fab', 'cc-jcb'],
        'amex': ['fab', 'cc-amex'],
        'visa': ['fab', 'cc-visa'],
        'unknown': ['fas', 'credit-card'],
        'discover': ['fab', 'cc-discover'],
        'diners': ['fab', 'cc-diners-club'],
        'mastercard': ['fab', 'cc-mastercard']
      }
      brand = cardBrandToClass[brand] ? brand : 'unknown'
      return cardBrandToClass[brand]
    },
    getImageUrl: function(item) {
      return getItemImageUrl(item, 'small')
    },
    setAltImg: function(event, item) {
      setFallbackImageUrl(event, item, 'small', true)
    },
    showDiscount: function(order) {
      return parseFloat(order.discount) > Number.EPSILON
    },
    getRelatedProducts: function() {
      axios.get('/campaign_products/', {
        params: {
          related_by__in: this.campaignProductIds
        }
      }).then(result => {
        console.log(result)
        this.related_products = result.data.results
      }, error => {
        console.error(error)
        this.$store.commit('critical_error', error)
        this.$store.commit('ready')
      })
    },
    contactSupport: function() {
      if (this.session && this.session.campaign && this.session.campaign.support_url) {
        if (this.session.campaign.allow_subdomain && this.session.campaign.url === this.session.campaign.support_url) {
          this.$store.commit('busy')
          this.$store.dispatch('home').then(() => {
            this.$router.go(0)
          }).catch(() => this.$store.commit('ready'))
        } else {
          window.location.replace(this.session.campaign.support_url)
        }
      } else {
        this.toggleContactSupport()
      }
    },
    done: function() {
      this.$emit('continue')
    },
    toggleContactSupport: function(success) {
      this.showContactForm = !this.showContactForm
      if (success) {
        this.contacted = true
        localStorage.setItem('customer_support', this.order.order_id)
        this.$store.commit('ready')
      }
    }
  },
  created() {
    this.$store.commit('busy')
    axios.get('/order_items/', {
      params: {
        is_active: this.activeItemRequired,
        checkout: this.isCheckout
      }
    }).then(result => {
      let items = result.data.results
      let lastOrderId = null
      let orders = []
      let order = {}
      let startIndex = 0

      for (let i = 0; i < items.length; i++) {
        let orderId = items[i].order.id
        if (orderId !== lastOrderId) { // if this is the 1st item of a different order
          if (i > 0) {
            // if this is not the 1st item, then copy the items from the previous order
            order['items'] = items.slice(startIndex, i)
            orders.push(order)
          }

          lastOrderId = orderId
          startIndex = i
          order = Object.assign({}, items[i].order)
        }
        this.campaignProductIds += items[i].campaign_product.id + ','
        if (i === (items.length - 1)) { // if this is the last item
          order['items'] = items.slice(startIndex)
          orders.push(order)
        }
      }
      if (orders.length > 0) {
        this.order = orders[0]
        this.contacted = localStorage.getItem('customer_support') === this.order.order_id
      }
      this.getRelatedProducts()
    }, error => {
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
    })

    if (this.session &&
      this.session.customer &&
      this.session.customer.payment_sources &&
      this.session.customer.payment_sources.length > 0
    ) {
      this.paymentSource = {
        type: this.session.customer.payment_sources[0].cc_type,
        last4: this.session.customer.payment_sources[0].cc_last4
      }
    } else {
      this.$store.dispatch('get_payment_sources')
        .then(paymentSources => {
          this.paymentSource = null
          for (let source of paymentSources) {
            if (source.is_default) {
              this.paymentSource = {
                id: source.id,
                isDefault: source.is_default,
                canUpdate: source.can_update && (source.payment_source === PaymentSource.CreditCard),
                isCard: source.payment_source === PaymentSource.CreditCard,
                source: PaymentSourceStrings[source.payment_source],
                type: source.cc_type,
                last4: source.cc_last4
              }
            }
          }
        })
        .catch(err => {
          this.$store.commit('critical_error', err)
          this.$store.commit('ready')
        })
    }
    this.$store.commit('ready')
  }
}
</script>

<style scoped lang="less">
  .order-summary-container {
    background-color: #ECF9E9;
    box-shadow: 0 3px 6px #00000020;
    border-radius: 4px;
    margin-top: 1rem;

    border-left-width: 0;
    border-right-width: 0;
    border-top-width: 0;

    h2 {
      font-size: 25px;
      font-weight: bold;
    }

    .rounded-circle {
      background-color: var(--theme_color);
    }

    .confirmation-email{
      color: var(--description_text_color);
      font-size: 11px;
    }

    .order-no {
      font-size: 12px;
    }
  }

  @media only screen and (min-width: 414px) {
    .order-summary-container {
      border-left-width: 1px;
      border-right-width: 1px;
      border-top-width: 1px;
    }
  }

  .order-summary-body {
    .order-detail{
      div {
        font-size: 14px;
      }
    }
    .order-date {
      font-size: 12px;
    }
    .table-header {
      font-size: 18px;
    }
    .order-summary-product-qty {
      color: #a7b0b1;
      font-size: 12px;
    }
    .product-img {
      width: 81px;
      height: 81px;
      object-fit: contain;
    }
    .price-detail {
      width: 50%;
      font-size: 13px;
    }
    .summary-btn-container {
      display: flex;
      margin-left: auto;
      justify-content: space-between;
      flex-direction: column;

      .continue-btn {
        margin-top: 1rem;
        margin-right: 0;
        margin-left: 0;
        width: 100%;

        .btn-icon {
          margin-right: 0.65rem;
        }
      }

      .continue-btn:first-child {
        margin-left: 0;
        flex: 1.3;
      }

      .continue-btn:last-child {
        margin-right: 0;
      }
    }
    .continue-btn {
      font-size: 16px;
      height: 48px !important;
      align-items: center;
      justify-content: center;
      display: flex;
      flex: 1;
      text-transform: capitalize;
      &-outline {
        background-color: #ffffff;
        color: var(--theme_color);
        border-color: var(--theme_color) !important;

        &:focus {
          box-shadow: none;
        }

        &:hover, &:active {
          color: #ffffff !important;
          border-color: #ffffff !important;
          background-color: var(--theme_color) !important;
          box-shadow: none;
        }
      }
    }
  }

  @media only screen and (min-width: 366px) {
    .order-summary-body {
      .price-detail {
        width: 100%;
        font-size: 14px;
      }
      .summary-btn-container {
        flex-direction: row;

        .continue-btn {
          margin-right: 0.5rem;
          margin-left: 0.5rem;

          &:first-child {
            flex: 1.5;
          }
        }
      }
    }
  }

  @media only screen and (min-width: 576px) {
    .order-summary-container {
      h2 {
        font-size: 28px;
      }
      .order-no {
        font-size: 18px;
      }
      .confirmation-email {
        font-size: 16px;
      }
    }
    .order-summary-body {
      .order-date {
        font-size: 14px;
      }
      .product-name{
        font-size: 16px;
      }
      .product-price {
        font-size: 16px;
      }
      .price-detail {
        width: 85%;
      }
      .summary-btn-container {
        .continue-btn {
          margin-top: 0.5rem;
          margin-left: 0.25rem;
          margin-right: 0.25rem;
        }
      }

      .continue-btn {
        font-size: 20px;
      }
    }
  }

  @media only screen and (min-width: 992px) {
    .order-summary-container {
      h2 {
        font-size: 30px;
      }
      .order-no {
        font-size: 20px;
      }
      .confirmation-email {
        font-size: 18px;
      }
    }
    .order-summary-body {
      .price-detail {
        width: 65%;
      }
    }
  }

  @media only screen and (min-width: 1200px) {
    .order-summary-body {
      .price-detail {
        width: auto;
      }
    }
  }

</style>
